import React, { useEffect, useState } from 'react';
import { getDomainTracking } from '../../services/domainService';
import { toDateValue } from '../../utils/relativeTime';
import { DOMAIN_STATUS_LABELS, DNS_STATUS_LABELS, TRACKING_STATUS_LABELS, StatusPill } from './domainStatus';

const Field = ({ label, children, mono = false }) => (
  <div className="min-w-0">
    <p className="text-xs text-base-content/60 uppercase tracking-wide">{label}</p>
    <p className={`mt-0.5 text-sm text-base-content break-words ${mono ? 'font-mono text-xs' : ''}`}>
      {children || '—'}
    </p>
  </div>
);

const SectionTitle = ({ children }) => (
  <h5 className="font-semibold text-base-content text-sm flex items-center gap-2">{children}</h5>
);

const fmtDate = (value) => {
  const date = toDateValue(value);
  return date ? date.toLocaleString('fr-FR') : null;
};

const Section = ({ title, icon, children }) => (
  <div className="border border-base-200 rounded-lg p-4">
    <SectionTitle>{icon} {title}</SectionTitle>
    <div className="mt-3 space-y-3">{children}</div>
  </div>
);

const InfoGrid = ({ items }) => (
  <div className="grid sm:grid-cols-2 gap-3">{items}</div>
);

/**
 * Détail complet d'un domaine : statut, provider (OpenProvider), DNS, email,
 * erreur de réservation et timeline des étapes (domainRegistrations/{id}).
 * @param {Object} props
 * @param {Object} props.domain - Document domaine (users/{uid}/domains/{id})
 */
const DomainDetails = ({ domain }) => {
  const [tracking, setTracking] = useState(null);
  const [trackingLoaded, setTrackingLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!domain?.id) return undefined;
    setTrackingLoaded(false);
    setTracking(null);
    getDomainTracking(domain.id)
      .then((data) => {
        if (!cancelled) setTracking(data);
      })
      .catch(() => {
        if (!cancelled) setTracking(null);
      })
      .finally(() => {
        if (!cancelled) setTrackingLoaded(true);
      });
    return () => { cancelled = true; };
  }, [domain?.id]);

  if (!domain) return null;

  const {
    domainName,
    status,
    registrationFailed,
    errorCode,
    errorMessage,
    supportRequired,
    failedAt,
    provider,
    providerDomainId,
    providerStatus,
    autoRenew,
    providerMetadata = {},
    dns = {},
    email = {}
  } = domain;

  const statusLabel = DOMAIN_STATUS_LABELS[status] || status || '—';
  const dnsLabel = dns.status === 'active'
    ? DNS_STATUS_LABELS.active
    : dns.status === 'failed'
      ? DNS_STATUS_LABELS.failed
      : dns.configured
        ? DNS_STATUS_LABELS.configured
        : DNS_STATUS_LABELS.not_configured;

  const nameServers = Array.isArray(providerMetadata.nameServers)
    ? providerMetadata.nameServers.filter(Boolean)
    : [];
  const mxRecords = Array.isArray(email.mx) ? email.mx : [];
  const dnsRecords = Array.isArray(dns.records) ? dns.records : [];
  const steps = tracking?.steps || {};

  const formatPrice = () => {
    const price = providerMetadata.price;
    if (price && typeof price === 'object') {
      return `${price.amount ?? ''} ${price.currency || 'EUR'}`.trim();
    }
    return price ? String(price) : null;
  };

  // Pare-feu d'affichage : OpenProvider renvoie parfois des objets {name, ip, ip6, seq_nr}.
  // On les formate en texte lisible (les docs anciens sont deja normalises par mapDomain).
  const formatNameServer = (ns) => {
    if (typeof ns === 'string') return ns;
    if (ns && typeof ns === 'object') {
      return [ns.name, ns.ip, ns.ip6].filter((v) => typeof v === 'string' && v.trim()).join(' · ');
    }
    return String(ns);
  };

  return (
    <div className="space-y-3 mt-3">
      {/* Bandeau d'erreur / verrou d'échec */}
      {(registrationFailed || status === 'registration_failed' || errorCode) && (
        <div className="bg-error/10 border border-error/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-error font-semibold text-sm">
            ⛔ Échec de l'enregistrement
            {supportRequired && <StatusPill label="Support requis" status="failed" />}
          </div>
          {errorCode && <p className="text-xs font-mono text-error/80 mt-1">{errorCode}</p>}
          {errorMessage && <p className="text-sm text-base-content mt-1">{errorMessage}</p>}
          {failedAt && (
            <p className="text-xs text-base-content/60 mt-1">
              Bloqué le {fmtDate(failedAt)} — ce verrou anti-doublon doit être supprimé par le support avant toute nouvelle tentative.
            </p>
          )}
        </div>
      )}

      {/* En-tête */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-lg font-semibold text-base-content font-mono">{domainName || '—'}</span>
        <StatusPill label={statusLabel} status={status} />
        {dns.status === 'active' && <StatusPill label={dnsLabel} status="active" />}
        {email.configured && <StatusPill label="Email configuré" status="active" />}
      </div>

      {/* Provider */}
      <Section title="Enregistrement (OpenProvider)" icon="🏷">
        <InfoGrid
          items={[
            <Field key="provider" label="Provider" mono>{provider}</Field>,
            <Field key="pid" label="ID OpenProvider" mono>{providerDomainId}</Field>,
            <Field key="pstatus" label="Statut provider">{providerStatus}</Field>,
            <Field key="autorenew" label="Renouvellement auto">
              {autoRenew ? 'Activé' : 'Désactivé'}
            </Field>,
            <Field key="price" label="Prix">{formatPrice()}</Field>,
            <Field key="registered" label="Enregistré le">{fmtDate(providerMetadata.registeredAt)}</Field>,
            <Field key="expiration" label="Expiration">{fmtDate(providerMetadata.expirationDate || domain.renewalDate)}</Field>,
            <Field key="owner" label="Owner handle" mono>{providerMetadata.ownerHandle}</Field>,
            <Field key="updated" label="Dernière mise à jour">{fmtDate(domain.updatedAt)}</Field>
          ]}
        />
        {nameServers.length > 0 && (
          <div className="border-t border-base-200 pt-3">
            <p className="text-xs text-base-content/60 uppercase tracking-wide mb-1">Serveurs DNS (registrar)</p>
            <ul className="space-y-1">
              {nameServers.map((ns, index) => (
                <li key={index} className="text-sm font-mono text-base-content break-all">{formatNameServer(ns)}</li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      {/* DNS */}
      <Section title="Configuration DNS" icon="🌐">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill
            label={dnsLabel}
            status={dns.status === 'active' ? 'active' : dns.status === 'failed' ? 'failed' : 'pending'}
          />
          {dns.zone && <span className="text-xs text-base-content/60">Zone : {dns.zone}</span>}
        </div>
        {dns.serverIp && <Field label="IP du serveur" mono>{dns.serverIp}</Field>}
        {dns.configuredAt && <Field label="Configuré le">{fmtDate(dns.configuredAt)}</Field>}
        {dns.error && (
          <div className="bg-error/10 border border-error/30 rounded-lg p-3 text-sm text-base-content">
            <span className="font-semibold text-error">Erreur DNS :</span> {dns.error}
          </div>
        )}
        {dnsRecords.length > 0 && (
          <div className="overflow-x-auto border border-base-200 rounded-lg">
            <table className="table table-sm w-full">
              <thead>
                <tr className="text-xs text-base-content/60">
                  <th className="px-3">Type</th>
                  <th className="px-3">Nom</th>
                  <th className="px-3">Valeur</th>
                  <th className="px-3 text-right">TTL</th>
                </tr>
              </thead>
              <tbody>
                {dnsRecords.map((record, index) => (
                  <tr key={index} className="border-t border-base-200">
                    <td className="px-3 font-mono text-xs">{record.type}</td>
                    <td className="px-3 font-mono text-xs">{record.name}</td>
                    <td className="px-3 font-mono text-xs break-all">{record.value}</td>
                    <td className="px-3 text-right text-xs">{record.ttl ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {dnsRecords.length === 0 && !dns.configured && (
          <p className="text-sm text-base-content/50">Aucun enregistrement DNS enregistré.</p>
        )}
      </Section>

      {/* Email */}
      <Section title="Configuration email" icon="✉">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill
            label={email.configured ? 'Configuré' : 'Non configuré'}
            status={email.configured ? 'active' : 'pending'}
          />
          {email.provider && <span className="text-xs text-base-content/60">Provider : {email.provider}</span>}
          {email.configuredAt && <span className="text-xs text-base-content/60">· {fmtDate(email.configuredAt)}</span>}
        </div>
        {email.error && (
          <div className="bg-error/10 border border-error/30 rounded-lg p-3 text-sm text-base-content">
            <span className="font-semibold text-error">Erreur email :</span> {email.error}
          </div>
        )}
        {email.spf && <Field label="SPF" mono>{email.spf}</Field>}
        {mxRecords.length > 0 && (
          <div>
            <p className="text-xs text-base-content/60 uppercase tracking-wide mb-1">Enregistrements MX</p>
            <ul className="space-y-1">
              {mxRecords.map((mx, index) => (
                <li key={index} className="text-sm font-mono text-base-content break-all">
                  {typeof mx === 'object' && mx !== null
                    ? Object.entries(mx).map(([k, v]) => `${k}=${v}`).join(' ')
                    : String(mx)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      {/* Timeline de réservation */}
      <Section title="Déroulé de la réservation" icon="🕐">
        {!trackingLoaded && <p className="text-sm text-base-content/50">Chargement du suivi…</p>}
        {trackingLoaded && !tracking && (
          <p className="text-sm text-base-content/50">
            Aucun suivi temps réel (document domainRegistrations/{domain.id}) pour cet enregistrement.
          </p>
        )}
        {trackingLoaded && tracking && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill label={TRACKING_STATUS_LABELS[tracking.status] || tracking.status} status={tracking.status} />
              {tracking.status === 'in_progress' && (
                <span className="text-xs text-base-content/60">
                  Étape {tracking.currentStep || 0}/{tracking.totalSteps || 10} · {tracking.currentStepLabel || ''}
                </span>
              )}
            </div>

            {tracking.status === 'in_progress' && (
              <div className="w-full bg-base-200 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${Math.round(((tracking.currentStep || 0) / (tracking.totalSteps || 10)) * 100)}%` }}
                />
              </div>
            )}

            {tracking.status === 'failed' && tracking.error && (
              <div className="bg-error/10 border border-error/30 rounded-lg p-3 text-sm text-base-content">
                <span className="font-semibold text-error">Échec du suivi :</span> {tracking.error}
              </div>
            )}

            <ol className="space-y-1.5">
              {Object.keys(steps)
                .sort((a, b) => Number(a) - Number(b))
                .map((stepKey) => {
                  const step = steps[stepKey];
                  if (!step) return null;
                  const detail = [
                    step.zone,
                    step.domainId,
                    step.domainStatus,
                    step.warning ? `⚠ ${step.warning}` : null,
                    step.error ? `✗ ${step.error}` : null
                  ].filter(Boolean).join(' · ');
                  return (
                    <li key={stepKey} className="flex items-start gap-2 text-sm">
                      <span className="text-success font-bold mt-0.5">✓</span>
                      <div className="min-w-0">
                        <p className="text-base-content font-medium">
                          <span className="text-base-content/50 mr-1">{stepKey}.</span>
                          {step.label || `Étape ${stepKey}`}
                        </p>
                        {detail && <p className="text-xs text-base-content/60 break-words">{detail}</p>}
                      </div>
                    </li>
                  );
                })}
            </ol>
          </>
        )}
      </Section>

      <p className="text-xs text-base-content/40">
        Document : users/{domain.siteId || '…'}/domains/{domain.id}
      </p>
    </div>
  );
};

export default DomainDetails;
